import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Download, RefreshCw, Search, ShieldCheck } from "lucide-react";
import Page from "../../components/Page";

const PERIODS = [
  { value: "day", label: "Oggi" },
  { value: "week", label: "Ultimi 7 giorni" },
  { value: "month", label: "Ultimi 30 giorni" },
  { value: "custom", label: "Periodo personalizzato" },
];

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function isoDaysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

function formatDate(value, includeTime = false) {
  if (!value) return "—";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("it-IT", includeTime
    ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
}

export default function RegistroSanificazione() {
  const [period, setPeriod] = useState("month");
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(29));
  const [dateTo, setDateTo] = useState(isoToday());
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, done: 0, later: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const params = useMemo(() => {
    const value = new URLSearchParams({ period, status });
    if (period === "custom") {
      value.set("date_from", dateFrom);
      value.set("date_to", dateTo);
    }
    if (query.trim()) value.set("q", query.trim());
    return value;
  }, [period, status, dateFrom, dateTo, query]);

  const load = useCallback(async () => {
    if (period === "custom" && (!dateFrom || !dateTo || dateFrom > dateTo)) {
      setError("Seleziona un intervallo di date valido.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/sanificazione/registro.php?${params.toString()}`, { credentials: "include" });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) throw new Error(json?.error || "Errore caricamento registro");
      setItems(Array.isArray(json.items) ? json.items : []);
      setSummary(json.summary || { total: 0, done: 0, later: 0 });
    } catch (err) {
      setError(err.message || "Errore caricamento registro sanificazione");
    } finally {
      setLoading(false);
    }
  }, [period, dateFrom, dateTo, params]);

  useEffect(() => {
    const timer = setTimeout(load, query ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, query]);

  const pdfUrl = useMemo(() => {
    const value = new URLSearchParams({ period });
    if (period === "custom") {
      value.set("date_from", dateFrom);
      value.set("date_to", dateTo);
    }
    return `/api/sanificazione/report-azienda-pdf.php?${value.toString()}`;
  }, [period, dateFrom, dateTo]);

  return (
    <Page title="Registro Sanificazione" back>
      <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 md:px-8">
        <div className="rounded-3xl bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white shadow-sm md:p-8">
          <div className="flex items-center gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/20 bg-white/15"><ShieldCheck size={28} /></div>
            <div>
              <h1 className="text-2xl font-bold md:text-3xl">Registro Sanificazione</h1>
              <p className="mt-1 text-sm text-white/85">Storico delle attività eseguite e posticipate, con operatore e data di registrazione.</p>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Summary label="Registrazioni" value={summary.total} />
          <Summary label="Eseguite" value={summary.done} tone="emerald" />
          <Summary label="Posticipate" value={summary.later} tone="amber" />
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-3 lg:grid-cols-[1fr_1fr_auto]">
            <div className="flex flex-wrap gap-2">
              {PERIODS.map((item) => (
                <button key={item.value} type="button" onClick={() => setPeriod(item.value)} className={`rounded-xl border px-3 py-2 text-sm font-semibold ${period === item.value ? "border-slate-950 bg-slate-950 text-white" : "hover:bg-slate-50"}`}>
                  {item.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1"><Search className="absolute left-3 top-3 text-slate-400" size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cerca attrezzatura o operatore" className="w-full rounded-xl border py-2.5 pl-10 pr-3 text-sm" /></div>
              <select value={status} onChange={(event) => setStatus(event.target.value)} className="rounded-xl border px-3 text-sm">
                <option value="all">Tutti gli esiti</option><option value="done">Eseguite</option><option value="later">Posticipate</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={load} className="rounded-xl border p-3 hover:bg-slate-50" title="Aggiorna"><RefreshCw size={17} /></button>
              <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-800"><Download size={17} /> PDF</a>
            </div>
          </div>
          {period === "custom" && (
            <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl bg-slate-50 p-3 text-sm">
              <CalendarDays size={18} className="text-slate-500" /><label>Dal <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="ml-2 rounded-lg border px-2 py-1" /></label><label>al <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="ml-2 rounded-lg border px-2 py-1" /></label>
            </div>
          )}
        </div>

        {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-800">{error}</div>}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[850px] text-left text-sm">
              <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600"><tr><th className="px-4 py-3">Data</th><th className="px-4 py-3">Attrezzatura / area</th><th className="px-4 py-3">Categoria</th><th className="px-4 py-3">Esito</th><th className="px-4 py-3">Operatore</th><th className="px-4 py-3">Registrata il</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">Caricamento registro...</td></tr> : items.length === 0 ? <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">Nessuna sanificazione registrata nel periodo selezionato.</td></tr> : items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50"><td className="px-4 py-3 font-semibold">{formatDate(item.giorno)}</td><td className="px-4 py-3 font-semibold text-slate-900">{item.attrezzatura}</td><td className="px-4 py-3 text-slate-600">{item.categoria || "—"}</td><td className="px-4 py-3"><Status status={item.status} /></td><td className="px-4 py-3">{item.operatore}</td><td className="px-4 py-3 text-slate-600">{formatDate(item.updated_at, true)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Page>
  );
}

function Summary({ label, value, tone = "slate" }) {
  const colors = tone === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-900" : tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-900" : "border-slate-200 bg-white text-slate-900";
  return <div className={`rounded-2xl border p-4 shadow-sm ${colors}`}><div className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</div><div className="mt-1 text-2xl font-bold">{value || 0}</div></div>;
}

function Status({ status }) {
  const done = status === "done";
  return <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${done ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-amber-200 bg-amber-100 text-amber-800"}`}>{done ? "ESEGUITA" : "POSTICIPATA"}</span>;
}
